// src/pages/Properties.js
import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useSearchParams as useReactRouterSearchParams, useNavigate } from 'react-router-dom'; // Aliased and added useNavigate
import './Properties.css';
import '../pages/PropertyForm.css';
import PropertyForm from './PropertyForm';
import { UserContext } from '../context/UserContext';
import { NotificationContext } from '../context/NotificationContext';
import { useMapContext } from '../context/MapContext'; // Import useMapContext
import { formatCurrencyForDisplay } from '../utils/currencyFormatter'; // Import formatCurrencyForDisplay
import { PropertyTypeNumericToStringMap } from '../utils/enums';
import { BASE_URL, getFullImageUrl } from '../utils/config'; // Import BASE_URL
import { apiFetch } from '../utils/apiClient';
import PropertyStatsModal from '../components/property/PropertyStatsModal';

// A simple hook to check for mobile screen size
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= breakpoint);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
};

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false); // State for stats modal
  const { filters } = useMapContext(); // Get filters from context
  const isInitialMount = useRef(true); // Ref to track initial mount
  const { addNotification } = useContext(NotificationContext);
  const isMobile = useIsMobile();

  const { user } = useContext(UserContext);
  const [urlSearchParams] = useReactRouterSearchParams(); // Hook for URL query params
  const navigate = useNavigate(); // Hook for navigation

  const isMyPropertiesView = urlSearchParams.get('view') === 'my';

  // Effect to redirect sellers from the general properties page to their own.
  useEffect(() => {
    // If the user is a seller but not an admin, and they are not on the 'my' view, redirect them.
    if (user.isSeller && !user.isAdmin && !isMyPropertiesView) {
      console.log('Redirecting seller to their "My Properties" view.');
      navigate('/properties?view=my', { replace: true });
    }
  }, [user.isSeller, user.isAdmin, isMyPropertiesView, navigate]);

  // Effect to load a property directly if its ID is in the URL
  useEffect(() => {
    const propertyIdFromUrl = urlSearchParams.get('propertyId');
    if (propertyIdFromUrl) {
      const fetchAndSelectProperty = async (id) => {
        setError(null);
        try {
          const url = `${BASE_URL}/api/properties/property/${id}`;
          const fetchOptions = {};
          if (user.token) {
            fetchOptions.headers = { Authorization: `Bearer ${user.token}` };
          }
          const response = await fetch(url, fetchOptions);
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Property not found.');
          }
          const propertyData = await response.json();
          setSelectedProperty(propertyData);
          setFormMode('view');
        } catch (err) {
          setError(err.message);
        }
      };

      fetchAndSelectProperty(propertyIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isSeller, user.isAdmin, isMyPropertiesView, navigate]);

  // Effect to reset to page 1 when filters change
  useEffect(() => {
    // We don't want to reset on the very first load of the component
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Any subsequent change to filters should reset the page
      setCurrentPage(1);
    }
  }, [filters]);

  const fetchProperties = useCallback(async () => {
    // Removed queryParams argument
    setIsLoading(true);
    setError(null);
    try {
      const itemsPerPage = user.itemsPerPage || 12;
      const isSelfScope = urlSearchParams.get('scope') === 'self';

      const filterEntries = Object.entries(filters)
        .map(([key, value]) => {
          if (value === undefined || value === null || String(value).trim() === '') return null;
          if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            // Don't include myPropertiesView in the query string
            if (key === 'myPropertiesView') return null;
            return Object.entries(value)
              .map(([subKey, subValue]) => {
                if (subValue === undefined || subValue === null || String(subValue).trim() === '') return null;
                return `${encodeURIComponent(key)}[${encodeURIComponent(subKey)}]=${encodeURIComponent(subValue)}`;
              })
              .filter(Boolean)
              .join('&');
          }
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .filter(Boolean);

      const paginationParams = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
      }).toString();

      let finalQueryString = filterEntries.join('&');
      if (finalQueryString && paginationParams) {
        finalQueryString += `&${paginationParams}`;
      } else if (paginationParams && !finalQueryString) {
        finalQueryString = paginationParams;
      } else if (!finalQueryString && !paginationParams) {
        finalQueryString = '';
      }

      // If this is the "my properties" view for an admin's own properties, add the scope parameter.
      if (isMyPropertiesView && isSelfScope) {
        finalQueryString = finalQueryString ? `${finalQueryString}&owner=self` : 'owner=self';
      }

      console.log('Fetching properties with query:', finalQueryString);

      let url;
      const fetchOptions = {};

      // If an admin is filtering by a specific seller, use the general endpoint.
      // Otherwise, use the dedicated endpoint for "My Properties" view.
      if (user.isAdmin && filters.sellerUsername) {
        url = `${BASE_URL}/api/properties?${finalQueryString}`;
        if (!user.token) throw new Error('You must be logged in to perform this search.');
        fetchOptions.headers = { Authorization: `Bearer ${user.token}` };
      } else if (isMyPropertiesView) {
        // The 'owner=self' parameter is now included in finalQueryString when needed.
        // Use the new secure endpoint for the seller's/admin's own properties view.
        url = `${BASE_URL}/api/properties/my-properties?${finalQueryString}`;
        if (!user.token) throw new Error('You must be logged in to view your properties.');
        fetchOptions.headers = { Authorization: `Bearer ${user.token}` };
      } else {
        // Public view, use the general endpoint.
        url = `${BASE_URL}/api/properties?${finalQueryString}`;
      }

      const response = await fetch(url, fetchOptions);
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to fetch properties: ${response.status} ${errorData}`);
      }
      const data = await response.json();
      setProperties(data.properties);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [filters, currentPage, user.itemsPerPage, user.token, user.isAdmin, isMyPropertiesView, urlSearchParams]);

  const handleDeleteProperty = (propertyID) => {
    setProperties((prevProperties) => prevProperties.filter((property) => property.propertyID !== propertyID));
    setSelectedProperty(null);
    setFormMode(null);
  };

  const handlePropertyClick = (property) => {
    console.log('Selected property:', property);
    setSelectedProperty(property);
    setFormMode('view');
  };

  const handleAddProperty = () => {
    setSelectedProperty(null);
    setFormMode('add');
  };

  const handleEditProperty = (property) => {
    setSelectedProperty(property);
    setFormMode('edit');
  };

  const handleCancel = () => {
    setSelectedProperty(null);
    setFormMode(null);
    setRefreshTrigger((prev) => prev + 1); // Refresh on cancel
  };

  const handleSuccess = (updatedProperty) => {
    // Property was successfully added or updated
    // Keep the form open in 'edit' mode with the new data
    setSelectedProperty(updatedProperty);
    // If the mode was 'add', it should now be 'edit'
    // If it was 'edit', it remains 'edit'
    setFormMode('edit');
    // Refresh the list in the sidebar
    setRefreshTrigger((prev) => prev + 1); // Trigger a refresh of the property list
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleClearAllCaches = async () => {
    if (window.confirm('Are you sure you want to clear the report cache for ALL properties? This action cannot be undone.')) {
      try {
        const response = await apiFetch(
          `${BASE_URL}/api/properties/clear-all-caches`,
          {
            method: 'DELETE',
          },
          user.token
        );

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to clear caches.');
        addNotification(data.message, 'success');
      } catch (err) {
        addNotification(err.message, 'error');
      }
    }
  };

  const openStatsModal = () => {
    setIsStatsModalOpen(true);
  };

  const closeStatsModal = () => {
    setIsStatsModalOpen(false);
  };

  useEffect(() => {
    // Don't fetch if a redirect is about to happen.
    // The redirect effect will trigger a re-render, and this effect will run again
    // with the correct URL parameters, preventing the initial unnecessary fetch.
    const isRedirecting = user.isSeller && !user.isAdmin && !isMyPropertiesView;
    if (isRedirecting) {
      console.log('Skipping initial fetch due to pending redirect.');
      return;
    }
    fetchProperties();
    // Add dependencies to re-evaluate this effect if user status or view changes.
  }, [fetchProperties, refreshTrigger, user.isSeller, user.isAdmin, isMyPropertiesView]);

  return (
    <div className={`properties-page-container ${isMobile && formMode ? 'is-viewing-mobile' : ''}`}>
      {/* Sidebar is removed */}
      <div className="properties-list-container">
        <div className="properties-list-header">
          <div className="properties-list-actions">
            {user.isSeller && (
              <button className="action-btn add-btn" onClick={handleAddProperty}>
                Add New Property
              </button>
            )}
            {user.isSeller && isMyPropertiesView && (
              <button className="action-btn stats-btn" onClick={openStatsModal}>
                Show Stats
              </button>
            )}
            {user.isAdmin && (
              <button className="action-btn delete-btn" onClick={handleClearAllCaches}>
                Clear All Cached Reports
              </button>
            )}
          </div>
        </div>

        {isLoading && <p>Loading properties...</p>}
        {error && <p>Error: {error}</p>}
        {!isLoading && !error && properties.length === 0 && <p>No properties found.</p>}
        <div className="property-list-scroll-area">
          {!isLoading &&
            !error &&
            properties.length > 0 &&
            properties.map((property) => (
              <div key={property.propertyID} className={`property-list-item ${selectedProperty?.propertyID === property.propertyID ? 'selected' : ''}`} onClick={() => handlePropertyClick(property)}>
                <div className="property-thumbnail-container">
                  {property.mainImageURL && (
                    <img
                      src={getFullImageUrl(property.mainImageURL)}
                      alt={property.address || 'Property'}
                      className="property-thumbnail"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  <p className="property-id">ID: {property.propertyID}</p>
                </div>
                <div className="property-info">
                  <p className="property-address">
                    {property.address}, {property.city}, {property.state}
                  </p>
                  <p className="property-details">
                    ( <strong style={{ color: '#1a3c5a' }}>{property.yearBuilt}</strong>){' '}
                    <strong style={{ color: '#1a3c5a' }}>
                      {PropertyTypeNumericToStringMap[property.type] || property.type} {property.beds} Beds/{property.baths}
                    </strong>{' '}
                    Baths <strong style={{ color: '#1a3c5a' }}>{formatCurrencyForDisplay(property.price)}</strong>
                  </p>
                </div>
              </div>
            ))}
        </div>
        <div className="pagination-container">
          <div className="pagination-buttons">
            <button onClick={handlePrevPage} disabled={currentPage === 1} className="pagination-button">
              &lt;
            </button>
            <span>{`Page ${currentPage} of ${totalPages}`}</span>
            <button onClick={handleNextPage} disabled={currentPage === totalPages} className="pagination-button">
              &gt;
            </button>
          </div>
        </div>
      </div>
      <div className="property-view-container">
        {formMode ? (
          <>
            {isMobile && (
              <div className="mobile-overlay-header">
                <button onClick={handleCancel} className="mobile-back-button">
                  &larr; Back to List
                </button>
              </div>
            )}
            <PropertyForm
              mode={formMode}
              property={selectedProperty}
              onDelete={handleDeleteProperty}
              propertyID={selectedProperty ? selectedProperty.propertyID : null}
              onCancel={handleCancel}
              onEdit={handleEditProperty}
              onSuccess={handleSuccess} // Pass the onSuccess handler
            />
          </>
        ) : (
          <p>Select a property to view details.</p>
        )}
      </div>
      {isStatsModalOpen && <PropertyStatsModal searchCriteria={filters} onClose={closeStatsModal} onPropertySelect={handlePropertyClick} />}
    </div>
  );
};

export default Properties;
